import type { Ref } from 'vue'
import type { WebChatMessage, ConfirmableItem, MessageCard, WorkflowRequest } from '~/types/web-chat'
import { setMessageCard } from '~/types/web-chat'
import { type ConfirmId } from '~/composables/web-chat/dispatch'
import { scopeLabel, type ChatScope } from '~~/shared/web-chat/scope'
import type { AttachableDocument } from '~/composables/web-chat/useChatAttachments'
import { normalizeJsonObject } from '~/composables/web-chat/normalizers'
import { workflowFieldsMeta, isBlankValue } from '~/utils/workflowFields'
import { filterCandidatesByKind } from '~/utils/workflowPartyBlocks'
import { parseEntityBind, normalizeFactProperty } from '~~/shared/templates/schema'
import { foldValue, valueForTarget } from '~~/shared/entities/partyValues'
import type { EntityCandidate, EntityCandidatesResponse } from '~~/shared/entities/entityCandidate'
import { asDownloadUrl } from '~/utils/downloadItemFile'
import { saveBlob } from '~/utils/saveBlob'
import { toolRunFailureText } from '~/utils/toolRunMessages'
import type { ToolRunListResponse } from '#shared/tools/tool'
import { useCasesStore } from '~/stores/cases'
import { buildConfirmCard, normalizeConfirmableItems } from '~/composables/web-chat/confirmCardBuilders'
import {
  applyToolRunToConversion,
  cardFromToolCall,
  isCardToolCall,
  savedWorkflowProposal,
  sourceFormatOf,
} from '~/composables/web-chat/confirmCardRehydration'

export interface ProposedEntitySlot {
  slot: string
  entityId?: string
  name?: string
}

interface BoundFieldRef {
  key: string
  slot: string
  property: string
  options?: unknown[]
}

export interface ConfirmCardsDeps {
  messages: Ref<WebChatMessage[]>
  requestScroll: () => void
  apiStreamFetch: (url: string, opts?: any) => Promise<Response>
  /** The chat's scope — labels the `clear_chat_scope` card. */
  activeScope: Ref<ChatScope>
  /** A single document search hit is attached as soon as its card renders. */
  attachDocument: (item: AttachableDocument) => void
}

export function useConfirmCards(deps: ConfirmCardsDeps) {
  const { messages, activeScope, attachDocument } = deps

  // Safe here: the only caller is the `web-chat` store's own setup, which already calls `useI18n()`.
  const { t } = useI18n()

  /**
   * The model sends every value as text (the tool schema has no other option — a heterogeneous
   * record is not expressible in the Gemini-accepted subset). The workflow's own declared type is
   * what says how to read it, so coercion happens HERE against the definition rather than being
   * trusted from the model.
   *
   * An uncoercible value is kept as the raw string rather than dropped: the field renders with the
   * text the agent supplied, the user sees something visibly wrong, and can fix it. Dropping it
   * would present an empty field and quietly lose what the conversation established.
   */
  function coerceInputValue(raw: string, input: { type?: string, constraints?: any }): unknown {
    const text = String(raw ?? '').trim()
    if (!text) return undefined

    switch (input.type) {
      case 'number': {
        const n = Number(text.replace(/[^\d.-]/g, ''))
        return Number.isFinite(n) ? n : text
      }
      case 'boolean':
        return /^(true|s[ií]|yes|1)$/i.test(text)
      case 'select': {
        // Match an option by value first, then by label, so the agent naming what the user would
        // read still lands on the right option id.
        const options: any[] = input.constraints?.options ?? []
        const hit = options.find(o => o.value === text)
          ?? options.find(o => String(o.label ?? '').toLowerCase() === text.toLowerCase())
        return hit?.value ?? text
      }
      case 'document_select':
        // The run endpoint takes an array for document slots even when only one is allowed.
        return [text]
      default:
        return text
    }
  }

  /**
   * Resolve a proposed run against the workflow's REAL inputs.
   *
   * The card renders immediately with `loading: true` and fills in a moment later, so a slow lookup
   * never leaves the thread with nothing in it. Org-scoped server-side; a workflow the caller cannot
   * see 404s and the card says so rather than showing an empty form.
   *
   * `proposed` is a PATCH, never the whole state (MAR-437 follow-up) — only the keys the model
   * actually sent THIS turn are coerced. `run_workflow` has no edit mode, so a follow-up proposal is
   * a BRAND NEW card/request object; without seeding it from the prior negotiation first, it would
   * start blank and lose everything an earlier turn or a party-fill pick already established. That
   * seed comes from `webChat.activeWorkflowRequest` — the SAME workflow's still-open card, found by
   * scanning the thread rather than kept in a separate mirror.
   *
   * Takes the MESSAGE id, never the raw `request` object the builder constructed (#236). By the time
   * this runs, `applyConfirmRequest` has already attached the card to `msg.card` on the reactive
   * `messages` array, so the object the builder held is a plain, no-longer-current snapshot — writing
   * `.loading`/`.fields`/etc. onto it mutates data nothing reads. Looking the card back up through
   * `messages.value` returns the Vue-reactive proxy the template actually renders from, so a write
   * that lands after the next turn has already started still repaints the card. (See the same
   * raw-vs-proxy distinction in the "prior" scan below, which predates this fix and already worked
   * around it for that one read.)
   */
  async function resolveWorkflowFields(
    messageId: string,
    proposed: Record<string, string>,
    saved?: Record<string, unknown>,
    entities: ProposedEntitySlot[] = [],
  ) {
    const liveCard = messages.value.find(m => m.id === messageId)?.card
    if (!liveCard || liveCard.toolId !== 'run_workflow') return // card gone (thread cleared) — nothing to resolve into
    const request = liveCard.request
    request.conversationMessageId = messageId
    let newValues: Record<string, unknown> = {}
    try {
      const { apiFetch } = useApi()
      const workflow = await apiFetch<any>(`/api/workflows/${request.workflowId}`)

      request.workflowName = workflow?.name || request.workflowName
      request.notices = workflow?.notices ?? undefined

      const rawInputsByKey = new Map<string, any>(
        (workflow?.inputs ?? []).filter((i: any) => i?.key).map((i: any) => [String(i.key), i]),
      )
      const slotKinds = new Map<string, string>(
        (workflow?.entities ?? []).filter((e: any) => e?.slot && e?.kind).map((e: any) => [String(e.slot), String(e.kind)]),
      )
      const fieldsMeta = workflowFieldsMeta(workflow)
      const boundBySlot = new Map<string, BoundFieldRef[]>()

      for (const meta of fieldsMeta) {
        const rawInput = rawInputsByKey.get(meta.key)
        const parsedBind = parseEntityBind(rawInput?.bind ?? meta.constraints?.bind)
        const src = meta.constraints?.entitySource
        const slot = parsedBind?.slot ?? src?.role ?? (typeof rawInput?.role === 'string' ? rawInput.role.trim() : undefined)
        const rawProp = parsedBind?.property ?? src?.field ?? rawInput?.field
        const idType = src?.idType ?? rawInput?.idType
        const property = normalizeFactProperty(rawProp, idType) ?? (typeof rawProp === 'string' ? rawProp.trim() : undefined)
        if (!slot || !property) continue
        const kind = slotKinds.get(slot) ?? meta.constraints?.partyKind ?? rawInput?.partyKind
        if (kind && !slotKinds.has(slot)) slotKinds.set(slot, String(kind))
        if (!meta.constraints?.entitySource) {
          meta.constraints = {
            ...(meta.constraints ?? {}),
            entitySource: {
              role: slot,
              field: property,
              ...(idType ? { idType } : {}),
            },
            ...(kind ? { partyKind: kind } : {}),
          }
        }
        const list = boundBySlot.get(slot) ?? []
        list.push({
          key: meta.key,
          slot,
          property,
          options: meta.options ?? meta.constraints?.options,
        })
        boundBySlot.set(slot, list)
      }

      for (const meta of fieldsMeta) {
        const supplied = proposed[meta.key]
        if (supplied === undefined) continue
        const coerced = coerceInputValue(supplied, meta)
        if (coerced !== undefined) newValues[meta.key] = coerced
      }

      const entityValues: Record<string, unknown> = {}
      if (!saved && boundBySlot.size > 0) {
        const entityBySlot = new Map(entities.map(e => [e.slot, e]))
        const slotsToResolve: Array<{ slot: string; entityId?: string; name?: string; fields: BoundFieldRef[] }> = []
        for (const [slot, slotFields] of boundBySlot.entries()) {
          const explicit = entityBySlot.get(slot)
          const nameField = slotFields.find(f => f.property === 'name')
          const scalarName = nameField && typeof newValues[nameField.key] === 'string'
            ? String(newValues[nameField.key]).trim()
            : undefined
          const entityId = explicit?.entityId
          const name = explicit?.name ?? scalarName
          if (entityId || name) {
            slotsToResolve.push({ slot, entityId, name, fields: slotFields })
          }
        }
        if (slotsToResolve.length > 0) {
          let candidates: EntityCandidate[] = []
          try {
            const scope = activeScope.value
            const scopeParams = scope.kind === 'case'
              ? { caseId: scope.id }
              : scope.kind === 'client'
                ? { clientId: scope.id }
                : {}
            const res = await apiFetch<EntityCandidatesResponse>('/api/entity-candidates', {
              query: { ...scopeParams, requireIdentifier: 'false' },
            })
            candidates = res?.candidates ?? []
          } catch {
            candidates = []
          }
          if (candidates.length > 0) {
            const metaByKey = new Map(fieldsMeta.map(m => [m.key, m]))
            for (const targetSlot of slotsToResolve) {
              const pool = filterCandidatesByKind(candidates, slotKinds.get(targetSlot.slot))
              let matched: EntityCandidate | undefined
              if (targetSlot.entityId) {
                matched = pool.find(c => c.entityId === targetSlot.entityId)
                  ?? candidates.find(c => c.entityId === targetSlot.entityId)
              }
              if (!matched && targetSlot.name) {
                const wantedFolded = foldValue(targetSlot.name)
                if (wantedFolded) {
                  matched = pool.find(c => foldValue(c.name) === wantedFolded)
                  if (!matched) {
                    const partial = pool.filter(c => {
                      const cf = foldValue(c.name)
                      return cf && (cf.includes(wantedFolded) || wantedFolded.includes(cf))
                    })
                    if (partial.length === 1) matched = partial[0]
                  }
                }
              }
              if (!matched) continue
              for (const bf of targetSlot.fields) {
                const rawVal = valueForTarget(matched, { field: bf.property, options: bf.options })
                if (isBlankValue(rawVal)) continue
                const meta = metaByKey.get(bf.key)
                const coerced = meta ? coerceInputValue(rawVal, meta) : rawVal
                if (coerced !== undefined) entityValues[bf.key] = coerced
              }
            }
          }
        }
      }

      // Seed from the prior open negotiation for the SAME workflow, if any. Scanned directly rather
      // than via `webChat.activeWorkflowRequest` — that computed returns only the SINGLE latest
      // `run_workflow` card, and this card's `request` is already attached to `msg.card` by the time
      // this runs (see `applyConfirmRequest`), so it would just find itself. Excluded by MESSAGE id,
      // not object identity: `messages.value`'s elements are Vue-reactive proxies, so `card.request`
      // read back through the array is a DIFFERENT reference from the raw `request` object the
      // builder closed over even when they are the same logical card — a `!==` identity check on
      // the request itself silently never excludes anything.
      const prior = [...messages.value].reverse()
        .find(m => m.id !== messageId && m.card?.toolId === 'run_workflow'
          && m.card.request.status !== 'declined' && m.card.request.workflowId === request.workflowId)
        ?.card as Extract<MessageCard, { toolId: 'run_workflow' }> | undefined
      const baseValues: Record<string, unknown> = {}
      if (prior) {
        for (const f of prior.request.fields) if (f.value !== undefined) baseValues[f.key] = f.value
      }
      // A proposal saved before a reload (MAR-988) is the WHOLE state the user left on the card —
      // already typed, edits included — so it wins outright: no coercion, no seeding.
      const merged = saved ?? { ...baseValues, ...entityValues, ...newValues }

      request.fields = fieldsMeta.map(meta => ({
        ...meta,
        value: merged[meta.key],
        prefilled: merged[meta.key] !== undefined,
      }))
    } catch (err) {
      console.warn('[webChatStore] Could not resolve the proposed workflow:', err)
      request.status = 'failed'
      request.error = t('chat.workflowCard.loadFailed')
    } finally {
      request.loading = false
    }
    // NOTHING is persisted here (MAR-988, CHAT-2.07). This used to POST `/draft` the moment the card
    // rendered (MAR-437 follow-up, "decision 1"), so a proposal carrying field values rendered a real
    // `.docx` into the library before the user had confirmed anything, and Cancelar left it behind.
    // Decision (Alex, 2026-10-01): no document exists until "Generar borrador"/"Ejecutar"
    // (`useWorkflowActions.acceptWorkflow`). Until then the card is its filled fields, and nothing else.
  }

  /**
   * Fill a conversion card's display name from the APP, not from the model.
   *
   * ONLY THE DOCUMENT NAME IS FETCHED (MAR-487). The tool's label and what it reads are in
   * `TOOL_PRESENTATION`, a code constant, so the card renders its final text on the first paint.
   * Under MAR-476 both were columns on a seeded `workflow_definitions` row resolved through
   * `GET /api/workflows/:id`, which meant two round trips and a card that existed briefly with blank
   * strings in it. The filename is still resolved rather than trusted: it comes from the model, and a
   * hallucinated one is a click on the wrong document.
   *
   * A failure is shown on the card rather than swallowed. `document_id` comes from the model, and an
   * id that does not resolve means the proposal is wrong — letting the user click Convert on it would
   * start a run that cannot succeed.
   *
   * Takes the MESSAGE id, not the raw `request` object the builder constructed — same reasoning as
   * `resolveWorkflowFields` above (#236): mutating the builder's raw object after `applyConfirmRequest`
   * has already attached the card to the reactive `messages` array writes to data nothing renders
   * from, so the card can be stuck on "Cargando…" forever if this resolves after the next turn starts.
   */
  async function resolveConversionCard(messageId: string) {
    const liveCard = messages.value.find(m => m.id === messageId)?.card
    if (!liveCard || liveCard.toolId !== 'convert_document') return // card gone (thread cleared) — nothing to resolve into
    const request = liveCard.request
    const { apiFetch } = useApi()
    try {
      const doc = await apiFetch<any>(`/api/items/${request.documentId}`)
      request.documentName = doc?.name || doc?.file_name || ''
      // The card states what it converts FROM (MAR-991) — the file's real format, not a guess.
      request.sourceFormat = sourceFormatOf(doc?.file_name) ?? sourceFormatOf(doc?.name)
    }
    catch (err) {
      // One localized sentence, not the server's. `request.error` is rendered verbatim by the card,
      // and `/api/items/:id` answers a missing or foreign id with an English operator message —
      // which is the same class of leak MAR-491 is about, arriving one step earlier.
      console.warn('[webChat] convert_document: could not resolve the proposed document:', err)
      request.error = t('clients.hub.convertResolveFailed')
    }
    finally {
      request.loading = false
    }
  }

  /**
   * Fill a `file_to_case` card's case name from the APP, not from the model (MAR-988).
   *
   * The link is written against `caseId`, so the name the user reads before confirming must be the
   * name of THAT case. Read through the cases store's list — never `GET /api/cases/:id`, which records
   * the case as opened in the recent-activity feed, a write this card must not cause before the user
   * has said yes. An id that resolves to nothing is shown as an error and the card cannot be accepted.
   */
  async function resolveFileToCaseCard(messageId: string) {
    const liveCard = messages.value.find(m => m.id === messageId)?.card
    if (!liveCard || liveCard.toolId !== 'file_to_case') return
    const request = liveCard.request
    try {
      const cases = useCasesStore()
      const found = cases.get(request.caseId) ?? (await cases.listCases()).find(c => c.id === request.caseId)
      if (found) request.caseName = found.name
      else request.error = t('webChat.confirmCards.fileToCase.notFound')
    }
    catch (err) {
      console.warn('[webChat] file_to_case: could not resolve the proposed case:', err)
      request.error = t('webChat.confirmCards.fileToCase.notFound')
    }
    finally {
      request.loading = false
    }
  }

  /**
   * The model's `values` array → a plain map, dropping anything malformed.
   *
   * An array of `{key, value}` pairs rather than a record because a free-form record is not
   * expressible in the Gemini-accepted schema subset (see `shared/web-chat/tools.ts`).
   */
  function proposedValues(input: Record<string, any>): Record<string, string> {
    const out: Record<string, string> = {}
    if (!Array.isArray(input.values)) return out
    for (const pair of input.values) {
      const key = String(pair?.key ?? '').trim()
      if (!key || pair?.value === undefined || pair?.value === null) continue
      out[key] = String(pair.value)
    }
    return out
  }

  /**
   * The model's `entities` array (`[{ slot, entity_id?, name? }]`) → sanitized slot assignments
   * (MAR-1107).
   */
  function proposedEntities(input: Record<string, any>): ProposedEntitySlot[] {
    if (!Array.isArray(input.entities)) return []
    const out: ProposedEntitySlot[] = []
    for (const item of input.entities) {
      if (!item || typeof item !== 'object') continue
      const slot = String(item.slot ?? '').trim()
      if (!slot) continue
      const entityId = typeof item.entity_id === 'string' && item.entity_id.trim()
        ? item.entity_id.trim()
        : typeof item.entityId === 'string' && item.entityId.trim()
          ? item.entityId.trim()
          : undefined
      const name = typeof item.name === 'string' && item.name.trim() ? item.name.trim() : undefined
      if (entityId || name) out.push({ slot, entityId, name })
    }
    return out
  }

  /**
   * Confirm tool call → the message's card. The card itself comes from `buildConfirmCard`
   * (`confirmCardBuilders.ts`), the same builder a reload uses, so the two cannot drift; what a LIVE
   * card also does when it appears — attach a single search hit, resolve the names the app owns —
   * is done here.
   */
  function applyConfirmRequest(msg: WebChatMessage, id: ConfirmId, input: Record<string, any>, clientToolId?: string) {
    const card = buildConfirmCard(id, input, clientToolId, { scopeLabel: scopeLabel(activeScope.value) ?? '' })
    // Single result → add to context immediately (still shown in the card as "in use").
    if (card.toolId === 'present_document_results' && card.request.docs.length === 1) {
      const onlyDoc = card.request.docs[0]!
      attachDocument({ id: onlyDoc.id, name: onlyDoc.name, file_name: null })
    }
    setMessageCard(msg, card)

    // Kick off async resolution AFTER `msg.card` is attached to the reactive `messages` array —
    // both resolvers look the card back up by message id and mutate the reactive proxy they find,
    // rather than the raw object the builder above closed over (#236). Doing this before the
    // assignment (as `run_workflow`/`convert_document` used to) meant a write landing after the next
    // turn had already started patched an object nothing renders from, leaving the card stuck on
    // "Cargando…" forever.
    if (id === 'run_workflow') void resolveWorkflowFields(msg.id, proposedValues(input), undefined, proposedEntities(input))
    else if (id === 'convert_document') void resolveConversionCard(msg.id)
    else if (id === 'file_to_case') void resolveFileToCaseCard(msg.id)
  }

  /**
   * Put a loaded conversation's cards back (MAR-991).
   *
   * Each confirm tool call is persisted as a `tool_calls` row and comes back on its message's
   * `toolCalls`; this turns the newest one on each message into its card, in the state the user left
   * it (`confirmCardRehydration.ts`), and removes every confirm call from `toolCalls` — they are the
   * card, and left there they would also render as a generic activity chip.
   *
   * Runs BEFORE `rehydrateWorkflowCards`, which only fills a message that has no card yet. Only ever
   * ADDS a card: one already on the message is live state. The names the app owns are resolved the
   * same way a live card resolves them; a conversion card then takes its status and result link from
   * the run its confirm started, matched on the message it came from.
   *
   * Best-effort: a failure leaves the card as rebuilt from its call rather than breaking the load.
   */
  async function rehydrateConfirmCards(conversationId: string) {
    const conversions: Array<{ messageId: string; documentId: string; toolId: string }> = []
    const proposals: Array<{ messageId: string; proposed: Record<string, string>; saved?: Record<string, unknown>; entities: ProposedEntitySlot[] }> = []
    for (const msg of messages.value) {
      const calls = msg.toolCalls ?? []
      const cardCalls = calls.filter(isCardToolCall)
      if (cardCalls.length === 0) continue
      const rest = calls.filter(tc => !isCardToolCall(tc))
      msg.toolCalls = rest.length ? rest : undefined
      if (msg.role !== 'assistant' || msg.card) continue

      // As live: a later card in the same turn replaces the earlier one, except that a result list
      // it displaces stays on screen as one of `earlierCards` (MAR-1017).
      const ctx = { scopeLabel: scopeLabel(activeScope.value) ?? '' }
      for (const earlier of cardCalls.slice(0, -1)) {
        if (earlier.name !== 'present_document_results') continue
        const earlierCard = cardFromToolCall(earlier, ctx)
        if (earlierCard) setMessageCard(msg, earlierCard)
      }
      const newest = cardCalls[cardCalls.length - 1]!
      const card = cardFromToolCall(newest, ctx)
      if (!card) continue
      setMessageCard(msg, card)
      if (card.toolId === 'convert_document') {
        void resolveConversionCard(msg.id)
        conversions.push({ messageId: msg.id, documentId: card.request.documentId, toolId: card.request.toolId })
      }
      else if (card.toolId === 'file_to_case' && card.request.loading) void resolveFileToCaseCard(msg.id)
      else if (card.toolId === 'run_workflow') {
        // An unconfirmed workflow proposal (MAR-988): its fields come back from what the user saved
        // on it, or — never edited — from the values the model sent. Nothing is persisted here.
        const input = (newest.input && typeof newest.input === 'object' && !Array.isArray(newest.input)) ? newest.input as Record<string, any> : {}
        proposals.push({
          messageId: msg.id,
          proposed: proposedValues(input),
          saved: savedWorkflowProposal(newest)?.values,
          entities: proposedEntities(input),
        })
      }
    }
    // In thread order, one at a time: a later proposal for the same workflow seeds from the earlier
    // card's fields, which must have resolved first.
    const proposalsRestored = (async () => {
      for (const p of proposals) await resolveWorkflowFields(p.messageId, p.proposed, p.saved, p.entities)
    })()
    if (conversions.length === 0) return proposalsRestored

    try {
      const { apiFetch } = useApi()
      const { runs } = await apiFetch<ToolRunListResponse>(`/api/tools/runs?conversationId=${encodeURIComponent(conversationId)}`)
      for (const run of runs ?? []) {
        // The exact match is the message the run was started from. A run started before that id was
        // recorded matches on the document and tool instead — within this conversation, so a guess
        // at worst names another of this thread's own conversions of that same file.
        const target = conversions.find(c => c.messageId === run.message_id)
          ?? (run.message_id ? undefined : conversions.find(c => c.documentId === run.source_item_id && c.toolId === run.tool_id))
        if (!target) continue
        const liveCard = messages.value.find(m => m.id === target.messageId)?.card
        if (liveCard?.toolId !== 'convert_document' || liveCard.request.status === 'cancelled') continue
        applyToolRunToConversion(liveCard.request, run)
        if (run.status === 'failed') liveCard.request.error = toolRunFailureText(t, run.error, liveCard.request.documentName)
      }
    }
    catch (err) {
      console.warn('[webChat] could not restore conversion card status:', err)
    }
    await proposalsRestored
  }

  /** The card still awaiting the user, newest first. `null` when nothing is pending. */
  function pendingCard(): { msg: WebChatMessage; card: MessageCard } | null {
    for (const msg of [...messages.value].reverse()) {
      if (msg.role !== 'assistant' || !msg.card) continue
      const status = (msg.card.request as { status?: string }).status
      if (status === 'pending') return { msg, card: msg.card }
    }
    return null
  }

  /** Mark a card settled. One path for every tool — the per-tool `confirmX`/`cancelX` pairs are gone. */
  function settleCard(card: MessageCard, status: 'confirmed' | 'cancelled') {
    ;(card.request as { status?: string }).status = status
  }

  // Triggers a real browser download of a single document. Marcus-only/GCS files are fetched as a
  // blob (auth-aware) and saved with their filename; DMS-sourced files 302 to an external Drive/
  // SharePoint URL where a cross-origin blob read fails, so we fall back to opening the redirect in
  // a new tab — a same-tab `a.href` navigation would work for the popup-blocker case, but the
  // `download` attribute is ignored cross-origin, so it would just navigate the whole chat tab away
  // to Drive/SharePoint and lose the conversation. A new tab risks being silently popup-blocked
  // outside the click's gesture window, but that is strictly better than destroying the chat.
  //
  // Returns whether the download actually happened, so a caller (e.g. a "Descargar" button) can
  // tell the difference between "saved" and "silently did nothing" rather than assuming success
  // just because the click fired.
  async function downloadDocument(item: ConfirmableItem): Promise<boolean> {
    const url = `/api/items/${item.id}/file`
    let succeeded = true
    try {
      // Bare `fetch`, not `apiStreamFetch`: the endpoint 302s to a signed GCS URL, and an
      // `Authorization` header would force a preflight on that cross-origin leg which the
      // bucket's GET/HEAD-only CORS config rejects. The `__session` cookie authenticates us.
      const res = await fetch(url, { credentials: 'same-origin' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      saveBlob(await res.blob(), item.name || 'document')
    } catch (err) {
      console.error('[webChat] downloadDocument: blob fetch failed, falling back to a new-tab navigation:', err)
      try {
        const a = document.createElement('a')
        // The `download=1` variant, NEVER the bare URL (AlexAmin/marcus#498). `url` is the preview
        // source too, so it answers `Content-Disposition: inline` by default: navigating there
        // renders the file in a tab, the user reads that as a download, and nothing lands on disk.
        // For a DMS file the flag is inert — that branch redirects to Drive/SharePoint, which owns
        // its own response — so it costs nothing on the case this fallback was built for.
        a.href = asDownloadUrl(url)
        a.target = '_blank'
        a.rel = 'noopener'
        document.body.appendChild(a)
        a.click()
        a.remove()
      } catch (navErr) {
        console.error('[webChat] downloadDocument: fallback navigation also failed:', navErr)
        succeeded = false
      }
    }
    const msg = [...messages.value].reverse().find(
      m => m.card?.toolId === 'download_documents' && m.card.request.items.some(i => i.id === item.id),
    )
    const req = msg?.card?.toolId === 'download_documents' ? msg.card.request : undefined
    if (succeeded && req && !req.downloaded.includes(item.id)) req.downloaded.push(item.id)
    return succeeded
  }

  return {
    normalizeConfirmableItems,
    applyConfirmRequest,
    rehydrateConfirmCards,
    downloadDocument,
    pendingCard,
    settleCard,
  }
}
